import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { FileText, Download, Calendar, DollarSign, Building2, Briefcase, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function MyTaxSlips() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [fiscalYears, setFiscalYears] = useState([]);
  const [taxData, setTaxData] = useState(null);
  const [agentInfo, setAgentInfo] = useState(null);

  useEffect(() => {
    loadData();
  }, [fiscalYear]);

  const loadData = async () => {
    setLoading(true);
    const token = getToken();
    
    try {
      // Get available fiscal years
      const yearsRes = await axios.get(`${API}/api/accounting/reports/fiscal-years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiscalYears(yearsRes.data.fiscal_years || []);

      // Get agent's tax slips data (now includes divisions)
      const slipsRes = await axios.get(`${API}/api/accounting/my-tax-slips?fiscal_year=${fiscalYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxData(slipsRes.data);

      // Get agent profile info
      const profileRes = await axios.get(`${API}/api/auth/agent-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentInfo(profileRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const downloadT4A = async (division = null) => {
    const token = getToken();
    try {
      // Add division parameter if specified
      let url = `${API}/api/accounting/t4a/generate/${user.agent_id}?fiscal_year=${fiscalYear}`;
      if (division) {
        url += `&division=${division}`;
      }
      
      const response = await axios.get(url, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `T4A_${fiscalYear}${division ? `_${division}` : ''}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('T4A slip downloaded successfully');
    } catch (err) {
      toast.error('Failed to download T4A slip');
    }
  };

  const downloadT4 = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API}/api/accounting/t4/generate/${user.agent_id}?fiscal_year=${fiscalYear}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `T4_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('T4 slip downloaded');
    } catch (err) {
      toast.error('Failed to download T4 slip');
    }
  };

  const emailT4AToMe = async () => {
    const token = getToken();
    try {
      toast.loading('Sending T4A to your email...', { id: 'email-t4a' });
      await axios.post(
        `${API}/api/accounting/t4a/email/${user.agent_id}?fiscal_year=${fiscalYear}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`T4A slip sent to ${agentInfo?.email}`, { id: 'email-t4a' });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send email';
      toast.error(errorMsg, { id: 'email-t4a' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const isEmployee = agentInfo?.employment_type === 'employee';
  const divisions = taxData?.divisions || [];
  const grandTotal = taxData?.grand_total || 0;
  const totalTransactions = taxData?.total_transactions || 0;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="my-tax-slips-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            My Tax Slips
          </h1>
          <p className="text-slate-500 mt-1">
            Download your T4/T4A slips for tax filing
          </p>
        </div>
        <Select value={fiscalYear.toString()} onValueChange={(v) => setFiscalYear(parseInt(v))}>
          <SelectTrigger className="w-32" data-testid="fiscal-year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fiscalYears.map(year => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Agent Info Card */}
      {agentInfo && (
        <Card>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase">Name</p>
                <p className="font-medium text-slate-900">{agentInfo.name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Email</p>
                <p className="font-medium text-slate-900">{agentInfo.email}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Employment Type</p>
                <Badge className={isEmployee ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                  {isEmployee ? 'Employee (T4)' : 'Contractor (T4A)'}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Tax Year</p>
                <p className="font-mono font-bold text-lg text-slate-900">{fiscalYear}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
              <div>
                <p className="text-emerald-100 text-sm">Total Commissions</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(grandTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Total Transactions</p>
                <p className="text-2xl font-bold">{totalTransactions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-purple-100 text-sm">Active Divisions</p>
                <p className="text-2xl font-bold">{divisions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Division Breakdown */}
      {divisions.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Commission by Division
            </CardTitle>
            <CardDescription>
              Your earnings breakdown by business division for {fiscalYear}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {divisions.map((div, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    div.division === 'mortgage' 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-teal-50 border-teal-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      div.division === 'mortgage' ? 'bg-blue-100' : 'bg-teal-100'
                    }`}>
                      {div.division === 'mortgage' ? (
                        <Building2 className={`w-6 h-6 ${div.division === 'mortgage' ? 'text-blue-600' : 'text-teal-600'}`} />
                      ) : (
                        <Briefcase className="w-6 h-6 text-teal-600" />
                      )}
                    </div>
                    <div>
                      <h3 className={`font-semibold ${div.division === 'mortgage' ? 'text-blue-900' : 'text-teal-900'}`}>
                        {div.division_label}
                      </h3>
                      <p className={`text-sm ${div.division === 'mortgage' ? 'text-blue-700' : 'text-teal-700'}`}>
                        {div.commission_count} transaction{div.commission_count !== 1 ? 's' : ''} • {div.slip_type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-2xl font-bold font-mono ${
                        div.division === 'mortgage' ? 'text-blue-600' : 'text-teal-600'
                      }`}>
                        {formatCurrency(div.total_commission)}
                      </p>
                      <Badge variant="secondary" className="mt-1">
                        Box 020
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No Commissions Found</h3>
            <p className="text-slate-500 mt-1">
              You don't have any commission records for {fiscalYear}.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Download Section */}
      {grandTotal > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Download className="w-5 h-5" />
              Download Tax Slips
            </CardTitle>
            <CardDescription>
              Download your official tax slips for filing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Combined T4A (All Divisions) */}
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-amber-900">
                      T4A - Combined Statement (All Divisions)
                    </h3>
                    <p className="text-sm text-amber-700">
                      Self-employed commissions (Box 020) • Total: {formatCurrency(grandTotal)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => downloadT4A()} 
                    data-testid="download-combined-t4a-btn" 
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download T4A
                  </Button>
                  <Button 
                    onClick={emailT4AToMe} 
                    data-testid="email-combined-t4a-btn" 
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    title={`Send to ${agentInfo?.email}`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email to Me
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Previously Generated Slips */}
      {taxData?.generated_slips?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-700">Previously Generated Slips</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Generated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taxData.generated_slips.map((slip) => (
                  <TableRow key={slip.id}>
                    <TableCell>
                      <Badge className={slip.slip_type === 'T4' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                        {slip.slip_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">{slip.fiscal_year}</TableCell>
                    <TableCell className="font-mono text-emerald-600">
                      {formatCurrency(slip.employment_income || slip.box_020_commissions || 0)}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(slip.generated_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Important Information
          </h4>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>• <strong>T4A Slips</strong> are for self-employed contractors with commission income (Box 020)</li>
            <li>• Your T4A includes commissions from ALL business divisions combined</li>
            <li>• Tax slips are required for filing your personal income tax return</li>
            <li>• Filing deadline is typically April 30th of the following year</li>
            <li>• If you have questions about your slip, please contact the administrator</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
